import { FC, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { X, FileText, AlertTriangle, Check } from 'lucide-react';
import { PageCard, SectionLabel, TOKENS } from '../../../components/design-system';
import { importStockCsv, csvTemplateUrl } from '../api';

interface Props {
  manufacturer_id: number;
  open: boolean;
  onClose: () => void;
}

export const ImportCsvModal: FC<Props> = ({ manufacturer_id, open, onClose }) => {
  const qc = useQueryClient();
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState<{
    created: Array<{ id: number; bluetooth: string; cmb_number: number; token: string }>;
    errors: Array<{ line: number; reason: string }>;
  } | null>(null);

  const importMut = useMutation(
    () => importStockCsv({ manufacturer_id, csv }),
    {
      onSuccess: (data) => {
        setResult(data);
        qc.invalidateQueries(['manufacturer-stock']);
        if (data.created.length > 0) {
          toast.success(`${data.created.length} caixa(s) criada(s). ${data.errors.length > 0 ? `${data.errors.length} linha(s) com erro.` : ''}`);
        } else if (data.errors.length > 0) {
          toast.warning(`Nenhuma caixa criada. ${data.errors.length} linha(s) com erro.`);
        }
      },
      onError: (e: any) => {
        toast.error(`Erro: ${e?.response?.data?.message ?? e.message}`);
      },
    },
  );

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setCsv(String(reader.result ?? ''));
      setResult(null);
    };
    reader.readAsText(file, 'utf-8');
  };

  const reset = () => { setCsv(''); setResult(null); };
  const close = () => { reset(); onClose(); };

  const downloadTokensCsv = () => {
    if (!result || result.created.length === 0) return;
    const lines = ['bluetooth,cmb_number,token'];
    for (const row of result.created) {
      // Quote token in case the JWT has any odd chars (it won't normally).
      lines.push(`${row.bluetooth},${row.cmb_number},"${row.token}"`);
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tokens-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '60px 20px', zIndex: 1000, overflowY: 'auto',
    }}>
      <div style={{ width: '100%', maxWidth: 720 }}>
        <PageCard>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <SectionLabel>Importar caixas em lote (CSV)</SectionLabel>
            <button type='button' onClick={close} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>
              <X size={18} color={TOKENS.color.muted} />
            </button>
          </div>

          {/* Inline help */}
          <div style={{ fontSize: 12, color: TOKENS.color.muted, lineHeight: 1.6, marginBottom: 12 }}>
            Limite de <strong>100 linhas</strong> por import.
            Header obrigatório: <code style={codeStyle}>bluetooth,cmb_number,intended_company_id,notes</code>.
            <br />
            <a href={csvTemplateUrl()} download style={{ color: TOKENS.color.action, fontWeight: 600 }}>
              📥 Baixar template de exemplo
            </a>
          </div>

          {!result ? (
            <>
              <Field label='Arquivo CSV'>
                <input
                  type='file'
                  accept='.csv,text/csv'
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFile(file);
                  }}
                  style={{ fontSize: 12 }}
                />
              </Field>

              <Field label='Ou cole o CSV diretamente'>
                <textarea
                  value={csv}
                  onChange={(e) => setCsv(e.target.value)}
                  rows={8}
                  placeholder={'bluetooth,cmb_number,intended_company_id,notes\nAA:BB:CC:DD:EE:F1,5001,,"primeira leva"\nAA:BB:CC:DD:EE:F2,5002,8,"para empresa X"'}
                  style={{
                    ...textareaStyle,
                    fontFamily: 'monospace',
                    fontSize: 11,
                  }}
                />
              </Field>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button type='button' onClick={close} style={ghostBtn}>Cancelar</button>
                <button
                  type='button'
                  disabled={!csv.trim() || importMut.isLoading}
                  onClick={() => importMut.mutate()}
                  style={{
                    ...primaryBtn,
                    opacity: (!csv.trim() || importMut.isLoading) ? 0.5 : 1,
                    cursor: (!csv.trim() || importMut.isLoading) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {importMut.isLoading ? 'Importando…' : 'Importar'}
                </button>
              </div>
            </>
          ) : (
            // === RESULT VIEW ===
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <Stat color={TOKENS.color.severity.low.text} icon={<Check size={16} />} label='Criadas' value={result.created.length} />
                <Stat color={TOKENS.color.severity.high.text} icon={<AlertTriangle size={16} />} label='Erros' value={result.errors.length} />
              </div>

              {result.created.length > 0 && (
                <div style={{
                  background: TOKENS.color.surface,
                  border: `1px solid ${TOKENS.color.border}`,
                  borderRadius: TOKENS.radius.chip,
                  padding: 10,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: TOKENS.color.muted, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                      Tokens gerados — baixe agora
                    </span>
                    <button
                      type='button'
                      onClick={downloadTokensCsv}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: TOKENS.color.action, color: '#fff', border: 'none',
                        padding: '4px 10px', fontSize: 11, fontWeight: 700,
                        borderRadius: TOKENS.radius.chip, cursor: 'pointer',
                      }}
                    >
                      <FileText size={12} /> Baixar tokens (CSV)
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: TOKENS.color.muted, lineHeight: 1.4 }}>
                    Os tokens estão visíveis aqui apenas agora. Você pode acessar individualmente
                    depois pela lista (botão "Ver token") enquanto a caixa estiver pendente.
                  </div>
                </div>
              )}

              {result.errors.length > 0 && (
                <div style={{
                  background: TOKENS.color.severity.high.bg,
                  border: `1px solid ${TOKENS.color.severity.high.text}33`,
                  borderRadius: TOKENS.radius.chip,
                  padding: 10,
                  maxHeight: 200, overflowY: 'auto',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: TOKENS.color.severity.high.text, marginBottom: 6 }}>
                    Linhas com erro:
                  </div>
                  {result.errors.map((err, i) => (
                    <div key={i} style={{ fontSize: 11, color: TOKENS.color.text, marginBottom: 2 }}>
                      <strong>Linha {err.line}:</strong> {err.reason}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type='button' onClick={() => { reset(); }} style={ghostBtn}>Importar outro</button>
                <button type='button' onClick={close} style={primaryBtn}>Fechar</button>
              </div>
            </div>
          )}
        </PageCard>
      </div>
    </div>
  );
};

const Stat: FC<{ color: string; icon: React.ReactNode; label: string; value: number }> = ({ color, icon, label, value }) => (
  <div style={{
    flex: 1, background: TOKENS.color.surface, border: `1px solid ${TOKENS.color.border}`,
    borderRadius: TOKENS.radius.chip, padding: '8px 12px',
    display: 'flex', alignItems: 'center', gap: 10,
  }}>
    <div style={{ color }}>{icon}</div>
    <div>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 10, fontWeight: 700, color: TOKENS.color.muted, letterSpacing: 0.4 }}>{label}</div>
    </div>
  </div>
);

const Field: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: TOKENS.color.muted }}>
      {label}
    </span>
    {children}
  </label>
);

const textareaStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 12,
  border: `1px solid ${TOKENS.color.border}`, borderRadius: TOKENS.radius.chip,
  background: TOKENS.color.card, color: TOKENS.color.text, outline: 'none',
  resize: 'vertical',
};
const primaryBtn: React.CSSProperties = {
  background: TOKENS.color.action, color: '#fff', border: 'none',
  padding: '8px 14px', fontSize: 13, fontWeight: 700, borderRadius: TOKENS.radius.chip, cursor: 'pointer',
};
const ghostBtn: React.CSSProperties = {
  background: TOKENS.color.card, color: TOKENS.color.text,
  border: `1px solid ${TOKENS.color.border}`,
  padding: '8px 14px', fontSize: 13, fontWeight: 600,
  borderRadius: TOKENS.radius.chip, cursor: 'pointer',
};
const codeStyle: React.CSSProperties = {
  fontFamily: 'monospace', fontSize: 11, padding: '1px 5px',
  background: TOKENS.color.surface, borderRadius: 3,
};
